import { Box, Card, Divider, Stack, Typography } from '@mui/material';

import Pill from '@/benefits-flow/Pill';
import { ACTION_TONE } from '@/benefits-flow/tones';
import { gbp } from '@/benefits-flow/format';
import { BENEFITS } from '@/benefits-flow/engine/benefits';
import { actionGroup } from '@/benefits-flow/reveal/selectors';
import {
  totalEstimatedValue,
  type Evaluation,
} from '@/benefits-flow/engine/evaluate';
import { ACTIONS } from '@/benefits-flow/BenefitsFlow.content';
import type { BenefitResult } from '@/benefits-flow/engine/types';

// The collection step — "here's how we actually claim this". The quiz asked
// quick bands; a real claim needs precise figures, so this shows the form shape
// per benefit, what already carries over from the quiz, and what we still
// gather. Destinations are stubs, not real forms (brief §8, §13).
export type CollectionProps = {
  evaluation: Evaluation;
  recipientName: string;
};

// What the quiz already captured (as bands / a nickname) that seeds a form.
const CAPTURED = [
  'Who they are (relationship)',
  'Nation',
  'Age band',
  'Help needs',
  'Condition',
  'Rough income & savings',
];
// What a real claim still needs precisely — the legal identity and exact figures
// the quiz deliberately skips. Today none of it pre-fills, because the screener
// and forms use different question-id namespaces (brief §14).
const STILL_NEEDED = [
  'Full legal name',
  'Exact date of birth',
  'Exact weekly income',
  'National Insurance number',
  'Bank details for payment',
];

const CollectionCard = ({ result }: { result: BenefitResult }) => {
  const info = BENEFITS[result.benefitId];
  if (!info) return null;
  const stub = info.applyStub;
  const action = ACTIONS[result.action];
  return (
    <Card variant='outlined'>
      <Stack sx={{ p: 2 }} spacing={1.5}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={1}
        >
          <Typography variant='subtitle2'>{info.name}</Typography>
          <Pill label={action.label} tone={ACTION_TONE[action.tone]} />
        </Stack>

        {stub ? (
          <>
            <Typography variant='body2' color='text.secondary'>
              {stub.questionCount} questions across {stub.sections.length}{' '}
              sections · {stub.estimate}
            </Typography>
            <Stack
              sx={{ flexWrap: 'wrap' }}
              direction='row'
              spacing={0.5}
              useFlexGap
            >
              {stub.sections.map((s) => (
                <Pill key={s} label={s} tone={ACTION_TONE.info} />
              ))}
            </Stack>
            {stub.note && (
              <Typography variant='caption' color='text.secondary'>
                {stub.note}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            {result.actionReason ??
              'No national form — we guide you through the local scheme.'}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

const Collection = ({ evaluation, recipientName }: CollectionProps) => {
  const actionable = [
    ...actionGroup(evaluation, ['APPLY_NOW']),
    ...actionGroup(evaluation, ['CHECK_AWARD', 'GUIDED']),
  ];
  const total = totalEstimatedValue(evaluation);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant='h3'>How we collect this</Typography>
        <Typography variant='body2' color='text.secondary'>
          The quiz used quick estimates. To claim, we gather the exact details —
          mostly from what you’ve already given us — and fill the forms for{' '}
          {recipientName}.
          {total > 0 && ` Up to ${gbp(total)} a year on the table.`}
        </Typography>
      </Box>

      {actionable.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Nothing to collect yet — the quiz didn’t land on anything we can act
          on.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {actionable.map((r) => (
            <CollectionCard key={r.benefitId} result={r} />
          ))}
        </Stack>
      )}

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Typography variant='overline' color='text.secondary'>
            Already captured in the quiz
          </Typography>
          <Stack sx={{ mt: 0.5 }} spacing={0.5}>
            {CAPTURED.map((c) => (
              <Typography key={c} variant='body2'>
                • {c}
              </Typography>
            ))}
          </Stack>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant='overline' color='text.secondary'>
            What we still gather exactly
          </Typography>
          <Stack sx={{ mt: 0.5 }} spacing={0.5}>
            {STILL_NEEDED.map((c) => (
              <Typography key={c} variant='body2'>
                • {c}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Stack>

      <Typography variant='caption' color='text.secondary'>
        Today none of the quiz answers actually pre-fill a form — the screener
        and the forms use different question-id namespaces. Bridging them is the
        win.
      </Typography>
    </Stack>
  );
};

Collection.displayName = 'Collection';
export default Collection;
